// Leaf-local pure ops — the transitions on a single Leaf (a pane's working set)
// and the one canonical ordered projection the keyboard folds walk.
//
// Split out of `Navigation/paneTree.ts` (C1 hotspot decomposition). Pure, and
// depends only on `keys.ts` + `types.ts` (no tree structure) — the tree-level
// ops in the entry file lift each of these through `mapLeaf`.

import type { ResourceId, ResourceRef, ResourceTab } from "../types.js";
import { keyOf, sameKey, seedConversationTab, tabHasKey } from "./keys.js";
import type { ConversationTab, Leaf, Tab, TabKey } from "./types.js";

// === Leaf-local pure ops =====================================================

// Activate a member key; a no-op (same reference) when the key is absent or
// already active.
export const leafActivate = (leaf: Leaf, key: TabKey): Leaf =>
    leaf.tabs.some((t) => tabHasKey(t, key)) && !sameKey(leaf.active, key)
        ? { ...leaf, active: key }
        : leaf;

// Append a tab, activating it; if a tab with the same key is already present
// the existing one is activated (dedup within the leaf — never a duplicate).
// Opening a resource that currently sits in the preview slot MOVES it out of
// the slot into the kept set (never both — the preview.id∉tabs invariant), so
// a kept open of a previewed resource promotes the glance rather than cloning.
export const leafAppend = (leaf: Leaf, tab: Tab): Leaf => {
    const key = keyOf(tab);
    if (leaf.tabs.some((t) => tabHasKey(t, key)))
        return leafActivate(leaf, key);
    const preview =
        leaf.preview !== null &&
        tab.type === "resource" &&
        tab.resource.id === leaf.preview.id
            ? null
            : leaf.preview;
    return { ...leaf, tabs: [...leaf.tabs, tab], preview, active: key };
};

// Insert (or move-within-leaf) a tab at `index`, activating it. Existing
// occurrences are removed first, so this is reorder AND insert.
export const leafInsertAt = (leaf: Leaf, tab: Tab, index: number): Leaf => {
    const key = keyOf(tab);
    const without = leaf.tabs.filter((t) => !tabHasKey(t, key));
    const at = Math.max(0, Math.min(index, without.length));
    return {
        ...leaf,
        tabs: [...without.slice(0, at), tab, ...without.slice(at)],
        active: key,
    };
};

// Remove a key; recompute active via the neighbour rule (previous, else next).
// Returns null when the leaf empties (caller prunes it); same reference when
// the key was absent.
export const leafRemoveKey = (leaf: Leaf, key: TabKey): Leaf | null => {
    const index = leaf.tabs.findIndex((t) => tabHasKey(t, key));
    if (index === -1) return leaf;
    const tabs = leaf.tabs.filter((t) => !tabHasKey(t, key));
    if (tabs.length === 0) return null;
    if (!sameKey(leaf.active, key)) return { ...leaf, tabs };
    const neighbour = tabs[index - 1] ?? tabs[index] ?? tabs[0];
    return { ...leaf, tabs, active: keyOf(neighbour) };
};

// === Preview-slot leaf ops (the glance lifecycle, pure) ======================
// The preview slot's four transitions, each on the Leaf. All total, all return
// a fresh leaf (or the SAME reference on a no-op). The invariant preview.id∉tabs
// is maintained here (open/promote clear the slot as the ref enters `tabs`).

// Set (replace) the pane's ONE preview to `ref`. If the ref is already a KEPT
// tab, previewing it is a no-op activate — a glance never DEMOTES a kept tab.
// Otherwise the slot adopts the ref (dropping any prior glance — at most one)
// and it becomes active.
export const leafOpenPreview = (leaf: Leaf, ref: ResourceRef): Leaf => {
    const key: TabKey = { type: "resource", id: ref.id };
    if (leaf.tabs.some((t) => tabHasKey(t, key)))
        return leafActivate(leaf, key);
    if (
        leaf.preview !== null &&
        leaf.preview.id === ref.id &&
        sameKey(leaf.active, key)
    )
        return leaf; // the same glance is already showing — no-op
    return { ...leaf, preview: ref, active: key };
};

// Promote the pane's preview to a KEPT tab (double-click / edit): append it,
// clear the slot, keep it active. No-op (same reference) when there is none.
export const leafPromotePreview = (leaf: Leaf): Leaf => {
    const ref = leaf.preview;
    if (ref === null) return leaf;
    const tab: ResourceTab = { type: "resource", resource: ref, dirty: false };
    return {
        ...leaf,
        tabs: [...leaf.tabs, tab],
        preview: null,
        active: { type: "resource", id: ref.id },
    };
};

// Close (drop) the pane's preview — never guarded (a preview is never dirty).
// When the preview was active, focus falls to the last kept tab; when the pane
// held nothing else, a fresh nascent conversation seeds it (a leaf is never
// empty). No-op (same reference) when there is none. A dropped preview is NOT
// remembered on any reopen stack (only deliberate closes of KEPT tabs are).
export const leafClosePreview = (leaf: Leaf): Leaf => {
    const ref = leaf.preview;
    if (ref === null) return leaf;
    const wasActive = sameKey(leaf.active, { type: "resource", id: ref.id });
    if (!wasActive) return { ...leaf, preview: null };
    const neighbour = leaf.tabs.at(-1);
    if (neighbour === undefined) {
        const conversation = seedConversationTab();
        return {
            ...leaf,
            tabs: [conversation],
            preview: null,
            active: { type: "conversation", id: conversation.id },
        };
    }
    return { ...leaf, preview: null, active: keyOf(neighbour) };
};

// An edit committed on the preview promotes it (edit-must-keep): the slot holds
// a dirty-less ref, so becoming dirty is only reachable AFTER promotion. No-op
// unless the edited id IS the current preview.
export const leafPreviewEdited = (leaf: Leaf, id: ResourceId): Leaf =>
    leaf.preview !== null && leaf.preview.id === id
        ? leafPromotePreview(leaf)
        : leaf;

// === leafOrder — the ONE canonical ordered projection ========================
// Root-cause fix (tab-model-keyboard-routing-root-cause.md, Fix A): `TabSet`
// (types.ts) is a LOSSY view of a leaf — `tabSetOfLeaf` drops the preview
// entirely, so every keyboard fold that used to walk `TabSet` (digit-jump,
// Cmd+9, Ctrl+Tab, ⌘W) was structurally blind to the glance, while the strip
// RENDER read the preview straight off the `Leaf`. Two sources of "what tabs
// exist" that could disagree. `leafOrder` is the single replacement: it reads
// the `Leaf` directly (the model that already, correctly, carries the
// preview) and lays out exactly what the strip renders, in render order.
// Home-less — `leafOrder` is the STRIP render order (home is pinned separately,
// never a rendered strip chip). The digit jumps and the tab-cycle index the
// HOME-LED `tabRing` built on top, so Home is tab 1 there (see `tabRing`).
export const leafOrder = (leaf: Leaf): readonly TabKey[] => {
    const resources = leaf.tabs.filter((t) => t.type === "resource").map(keyOf);
    // RATIFIED (owner): the digit chords must reach the glance — it is
    // spliced in right after the kept resources, exactly the slot the strip
    // renders it in (ResourceTabs.tsx, the preview chip between the open
    // resources and the quality tabs).
    const preview: readonly TabKey[] =
        leaf.preview !== null
            ? [{ type: "resource", id: leaf.preview.id }]
            : [];
    const quality = leaf.tabs.filter((t) => t.type === "quality").map(keyOf);
    const hasTab = (type: Tab["type"]): boolean =>
        leaf.tabs.some((t) => t.type === type);
    const conversations = leaf.tabs
        .filter((t): t is ConversationTab => t.type === "conversation")
        .map(keyOf);
    return [
        ...(hasTab("home") ? [{ type: "home" } satisfies TabKey] : []),
        ...(hasTab("studio") ? [{ type: "studio" } satisfies TabKey] : []),
        ...(hasTab("projects") ? [{ type: "projects" } satisfies TabKey] : []),
        ...(hasTab("knowledge")
            ? [{ type: "knowledge" } satisfies TabKey]
            : []),
        ...(hasTab("search") ? [{ type: "search" } satisfies TabKey] : []),
        ...resources,
        ...preview,
        ...quality,
        ...conversations,
        ...(hasTab("settings") ? [{ type: "settings" } satisfies TabKey] : []),
    ];
};

// The digit-jump / tab-cycle ring IS the strip's render order (`leafOrder`) now
// there is no pinned home to lead it: Cmd+1 = the first strip chip (a hero when
// one leads, else the first resource), Cmd+2 the next, and Ctrl+Tab wraps
// through every member.
const tabRing = (leaf: Leaf): readonly TabKey[] => leafOrder(leaf);

// Jump to the Nth tab as the author counts the strip, 1-based: Cmd+1 = the first
// chip, … a glance counts as a slot too. Out of range ⇒ undefined, so the caller
// no-ops rather than inventing a tab.
export const leafTabAtIndex = (
    leaf: Leaf,
    index: number,
): TabKey | undefined => (index >= 1 ? tabRing(leaf)[index - 1] : undefined);

// The last tab in the strip (Cmd+9) — the leaf's active tab when the ring is
// (impossibly) empty, since a leaf always holds at least one tab.
export const leafLastTab = (leaf: Leaf): TabKey => {
    const ring = tabRing(leaf);
    return ring[ring.length - 1] ?? leaf.active;
};

// Ctrl+Tab / Ctrl+Shift+Tab: step the active tab one place through the home-led
// `tabRing`, wrapping at either end. `leaf.active` is ALREADY a `TabKey` (no
// `ActiveTab` round-trip needed), and `sameKey` — not a hand-rolled per-variant
// comparison — finds its slot, so an active preview (whose key is bit-for-bit a
// resource key) is found exactly like a kept tab rather than collapsing to home
// via a failed `findIndex`.
export const leafCycleTab = (leaf: Leaf, step: 1 | -1): TabKey => {
    const ring = tabRing(leaf);
    const current = ring.findIndex((k) => sameKey(k, leaf.active));
    const index = (Math.max(current, 0) + step + ring.length) % ring.length;
    return ring[index] ?? leaf.active;
};

// === activateKeyInFocused's leaf primitive ===================================
// `leafActivate` (above) only knows about `leaf.tabs` — a key naming the preview
// is, by construction, never a member, so it silently no-op'd there exactly like
// the old TabSet-backed `activateTab` (types.ts) did. `leafActivateKey` widens
// the check by one clause: a key matching the preview's id moves the `active`
// pointer onto it (the slot itself is never touched — activating a glance does
// not promote it). This is what makes a digit/cycle target resolved via
// `leafOrder` actually LAND, closing the "reaches but doesn't activate" half of
// Fix A.
export const leafActivateKey = (leaf: Leaf, key: TabKey): Leaf => {
    if (leaf.tabs.some((t) => tabHasKey(t, key)))
        return leafActivate(leaf, key);
    if (
        leaf.preview !== null &&
        sameKey(key, { type: "resource", id: leaf.preview.id })
    )
        return sameKey(leaf.active, key) ? leaf : { ...leaf, active: key };
    return leaf;
};
